import { WorkShiftMapper } from "@/adapters/WorkShift.mapper";
import { supabase } from "@/utils/supabase";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
/**
 * Concrete implementation of WorkShiftsService
 * Handles all database operations for work shifts
 */
export class WorkShiftsService {
    constructor() {
        this.table = "WorkShifts";
    }
    async getShiftsWithParams(params) {
        const { page, pageSize, filters, sorters } = params;
        const offset = (page - 1) * pageSize;
        let query = supabase()
            .from(this.table)
            .select("*", { count: "exact" })
            .eq("isDeleted", false);
        // Apply filters
        if (filters) {
            if (filters.idTechnicians && filters.idTechnicians.length > 0) {
                query = query.in("idTechnician", filters.idTechnicians);
            }
            if (filters.status && filters.status.length > 0) {
                query = query.in("status", filters.status);
            }
        }
        // Apply sorting
        if (sorters) {
            Object.entries(sorters).forEach(([field, order]) => {
                if (order) {
                    query = query.order(field, { ascending: order === "ascend" });
                }
            });
        }
        else {
            // Default sorting by planned start time
            query = query.order("plannedStartTime", { ascending: false });
        }
        // Apply pagination
        query = query.range(offset, offset + pageSize - 1);
        const { data, error, count } = await query;
        if (error)
            throw error;
        return {
            data: (data || []).map(WorkShiftMapper.toDomain),
            total: count || 0,
            page,
            pageSize,
        };
    }
    async getShiftsByIds(ids) {
        const { data, error } = await supabase()
            .from(this.table)
            .select("*")
            .in("id", ids)
            .eq("isDeleted", false);
        if (error)
            throw error;
        return (data || []).map(WorkShiftMapper.toDomain);
    }
    async getShiftById(id) {
        const { data, error } = await supabase()
            .from(this.table)
            .select("*")
            .eq("id", id)
            .eq("isDeleted", false)
            .single();
        if (error)
            throw error;
        if (!data)
            throw new Error("WorkShift not found");
        return WorkShiftMapper.toDomain(data);
    }
    async createShift(data) {
        // Validation: plannedEnd must be after plannedStart
        if (data.plannedEndTime.isBefore(data.plannedStartTime) ||
            data.plannedEndTime.isSame(data.plannedStartTime)) {
            throw new Error("Время окончания смены должно быть позже времени начала");
        }
        // Check for overlapping shifts for the same technician
        const existing = await this.getShiftsWithParams({
            page: 1,
            pageSize: 1000, // Get all shifts to check overlaps
            filters: {
                idTechnicians: [data.idTechnician],
            },
        });
        const overlaps = existing.data.some((ws) => {
            // Check if intervals overlap: !(newEnd <= existingStart || newStart >= existingEnd)
            return !(data.plannedEndTime.isSameOrBefore(ws.plannedStartTime) ||
                data.plannedStartTime.isSameOrAfter(ws.plannedEndTime));
        });
        if (overlaps) {
            throw new Error("Смена пересекается с существующей сменой этого техника");
        }
        // Convert to DTO format for database insert
        const dto = WorkShiftMapper.toCreateWorkShiftDto(data);
        const { data: inserted, error } = await supabase()
            .from(this.table)
            .insert(dto)
            .select("*")
            .single();
        if (error)
            throw error;
        if (!inserted)
            throw new Error("Failed to create work shift");
        return WorkShiftMapper.toDomain(inserted);
    }
    async updateShift(id, data) {
        // Validation: plannedEnd must be after plannedStart
        if (data.plannedEndTime.isBefore(data.plannedStartTime) ||
            data.plannedEndTime.isSame(data.plannedStartTime)) {
            throw new Error("Время окончания смены должно быть позже времени начала");
        }
        // Check for overlapping shifts for the same technician (excluding current shift)
        const existing = await this.getShiftsWithParams({
            page: 1,
            pageSize: 1000, // Get all shifts to check overlaps
            filters: {
                idTechnicians: [data.idTechnician],
            },
        });
        const overlaps = existing.data
            .filter((ws) => ws.id !== id) // Exclude current shift from overlap check
            .some((ws) => {
            return !(data.plannedEndTime.isSameOrBefore(ws.plannedStartTime) ||
                data.plannedStartTime.isSameOrAfter(ws.plannedEndTime));
        });
        if (overlaps) {
            throw new Error("Смена пересекается с существующей сменой этого техника");
        }
        // Convert to DTO format for database update
        const dto = WorkShiftMapper.toCreateWorkShiftDto(data);
        const { data: updated, error } = await supabase()
            .from(this.table)
            .update(dto)
            .eq("id", id)
            .select("*")
            .single();
        if (error)
            throw error;
        if (!updated)
            throw new Error("WorkShift not found");
        return WorkShiftMapper.toDomain(updated);
    }
    async deleteShift(id) {
        const { error } = await supabase()
            .from(this.table)
            .update({ isDeleted: true })
            .eq("id", id);
        if (error)
            throw error;
    }
    async copyShiftNextDay(shiftId) {
        const { data, error } = await supabase()
            .from(this.table)
            .select("*")
            .eq("id", shiftId)
            .eq("isDeleted", false)
            .single();
        if (error || !data)
            throw new Error("Смена не найдена");
        const domain = WorkShiftMapper.toDomain(data);
        // Create a copy with times shifted by 1 day
        return this.createShift({
            idTechnician: domain.idTechnician,
            plannedStartTime: domain.plannedStartTime.add(1, "day"),
            plannedEndTime: domain.plannedEndTime.add(1, "day"),
            actualStartTime: domain.actualStartTime
                ? domain.actualStartTime.add(1, "day")
                : null,
            actualEndTime: domain.actualEndTime
                ? domain.actualEndTime.add(1, "day")
                : null,
            status: domain.status,
        });
    }
    async getShiftsByTechnicianId(technicianId) {
        const { data, error } = await supabase()
            .from(this.table)
            .select("*")
            .eq("idTechnician", technicianId)
            .eq("isDeleted", false);
        if (error)
            throw error;
        if (!data)
            throw new Error("Смены не найдены");
        return data.map(WorkShiftMapper.toDomain);
    }
}
// Export singleton instance
export const workShiftsService = new WorkShiftsService();
